import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import Mandala from "@/components/Mandala";
import SEO from "@/components/SEO";
import InsuranceCallout from "@/components/InsuranceCallout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { usePageImages } from "@/lib/pageImages";
import GuestCheckoutModal from "@/components/checkout/GuestCheckoutModal";

const SLUG = "online-restorative-aerial-yoga-teacher-training";
const HERO = "/api/files/79f0b8d1-6c49-4eac-8467-b49430cc1426";
// Poster shown over the Vimeo iframe until the visitor taps play — overrides
// the "Copyright" splash Vimeo displays as the default thumbnail. Same
// on-brand image as the hero so the video feels like a natural extension.
const WELCOME_POSTER = HERO;
const LIVE = "https://avianayoga.com/api/files";
const IMG_DEFAULTS = {
  hero: HERO,
  "welcome-poster": WELCOME_POSTER,
  "why-photo": `${LIVE}/f914e7b6-8b8e-4d5b-92ce-a067cfb53f23`,
  "founder-call": `${LIVE}/24f89823-48a4-41d5-8816-74efa3b5ec84`,
  "curriculum-photo": `${LIVE}/e591782e-1635-45ca-87f1-99d18d79a7c3`,
  "requirements-photo": `${LIVE}/1a8b0c46-57a4-4ec2-8101-ada9b2c5d044`,
  "restorative-band": `${LIVE}/6041573a-10f6-4efd-b91c-cdeb07e2a312`,
  "closing-portrait": `${LIVE}/6a0fc213-002f-4583-aec5-c3facc71807a`,
};
const CLASS_IMG = "/api/files/c40cb2ef-b933-40cb-8519-fa6478cd7cc7";
const ASANA_IMG = "/api/files/3855ea74-337b-49ae-9d44-47a3d6bb4a43";
const HOLLY_IMG = "/api/files/2954dd4f-d431-4797-a7c8-838938f7b6cb";

const INCLUDED = [
  "Lifetime access to all videos & online materials",
  "A 90-minute Restorative Aerial Yoga class to experience",
  "36 in-depth asana videos with variations",
  "Equipment overview & hammock-tying demonstrations",
  "4 foundational principles videos — importance of restoration, contraindications, Aviana methodology",
  "4 sensory support lectures: Color Therapy · Sound Healing · Aromatherapy · Chakra Integration",
  "Full-color training manual printed & shipped to your door",
  "30-minute private 1:1 video call with Holly",
  "Sisters of the Silks exclusive Aviana community access",
];

const CURRICULUM = [
  {
    title: "Foundations",
    body: "Discover the guiding galactic principles of Aviana that create the foundation for unique and mindful restorative aerial yoga practices.",
    bullets: [
      "Introduction to the Mythical Goddess Tarot Deck & Mudra Deck for intentional class sequencing",
      "The importance of restoration — why it is so necessary right now",
      "Who restorative aerial yoga is for & contraindications to be aware of",
    ],
  },
  {
    title: "Equipment Overview",
    body: "Everything you need to feel confident in your students' safety and your own setup.",
    bullets: [
      "Single-point and double-point aerial yoga hammock setups",
      "The simple, repeatable method for tying your hammock so you feel confident every time",
    ],
  },
  {
    title: "Restorative Aerial Yoga Class",
    body: "Experience an Aviana-style 90-minute restorative aerial yoga class from the inside.",
    bullets: ["Move through the full class as a student so you know what you'll be giving"],
  },
  {
    title: "Asana Tutorials",
    body: "36 asana tutorials with timing, variations, and the energetic intention behind each posture.",
    bullets: [
      "The 3 types of Aviana Restorative Asanas",
      "Ideal duration to hold each asana",
      "Practice & review videos for every posture",
    ],
  },
  {
    title: "Sensory Support Tools",
    body: "Full-sensory integration — the signature of every Aviana class.",
    bullets: [
      "Chakra Affirmation Exercise — bringing energy centers into your class",
      "Color therapy as a therapeutic enhancement",
      "Sound healing & resonant frequencies",
      "Aromatherapy for the final layer of presence",
    ],
  },
];

const CERTIFICATION_MODULES = [
  { num: "01", title: "Examination + Essays", body: "A short, straightforward exam covering everything in the videos & manual, plus 2 brief essays. One retake permitted if needed." },
  { num: "02", title: "Research Project", body: "Design a 4-class restorative aerial yoga series for a population of your choosing (60–90 min each). Use it as the series you launch when you start sharing this practice." },
  { num: "03", title: "Practice Teaching Hours", body: "Submit 5 practice teaching hours with self-evaluation + student evaluations. Can be completed with just one student, though teaching multiple bodies is encouraged." },
  { num: "04", title: "Teaching Demonstration", body: "Submit a short video demonstrating the teaching skills you've acquired through the training." },
];

const FAQS = [
  { q: "What is the Aviana style of aerial yoga?", a: "Aviana offers a practice where you maintain a connection with the hammock throughout — surrendering into being held and supported while flowing through a moving meditation. We give great attention to body alignment and the relationship between the body and the anchor points. From a rooted connection, the practice takes on full sensory engagement through the instructor's attentiveness to sights, sounds, and aromas. It's a full immersion experience designed to guide the practitioner into a meditative journey of growth and awareness with lengthened, toned elegance." },
  { q: "I've never taken an aerial yoga class — is this okay for me?", a: "Absolutely. You wouldn't be alone — many instructors enroll in this online training to experience aerial yoga for the first time. Since aerial is presented in so many ways, sometimes it's better to enter with a beginner's mind." },
  { q: "Where can I do my practice teaching hours?", a: "Your practice teaching hours are important to your development. We'll discuss this in the training to make sure you have a place where you can hang at least two hammocks. If you live near a studio that hosts Aviana trainings, there's often an arrangement set up for you to complete your hours there." },
  { q: "How soon can I finish & receive my certification?", a: "Some students complete the program and remaining modules within 2 months of enrolling. You have 1 year from enrollment to complete your certification work — and lifetime access to the videos and materials." },
  { q: "How is this different from other aerial yoga teacher trainings?", a: "Aviana focuses on optimal body alignment, moving meditative sequences, full sensory engagement, and the connection to the power of sisterhood. The training is presented as a retreat infused with a teacher training — so you can immerse yourself in your own growth and expansion as you discover your wings." },
  { q: "I struggle with test taking — how hard is the exam?", a: "The examination module is very straightforward. Everything on it is included in the training videos and your manual. It's a short exam — not something to stress about. You'll have one retake available in the unlikely event you don't pass on the first try." },
  { q: "Will I receive support after the training?", a: "Yes. Aviana is here to support you through every step of the journey. Reach out anytime, or lean into the Sisters of the Silks Community Group." },
  { q: "Once I'm certified, where can I teach?", a: "Once certified, you've discovered your wings to take you where you'd like to go. Since the launch of the Aviana program, studios, park districts, retreat centers, gyms, health clubs, and therapy centers have installed hammocks to offer classes. If there isn't a space in your area, Aviana offers consulting to help support the launch of new spaces." },
];

const COURSE_TESTIMONIALS = [
  { name: "Marcella Castillo", location: "United States", quote: "I was pleasantly surprised with how much this online course covered and how easy it was to navigate through the modules and videos. I love how you separated them so it's easy to jump right back in and resume the training where I left off. None of the videos were too long. I was really impressed overall." },
  { name: "Bozena Bubonja", location: "Croatia", quote: "I am very happy that I passed the exam and received the certificate so I can introduce my students to the wonderful world of restorative yoga. Now I am even more relaxed and more motivated to continue learning the restorative story. Thank you very much for your support, encouragement, and the wonderful program you have designed." },
];

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-aviana-border/70 last:border-b-0" data-testid={`faq-${idx}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-6 py-6 text-left">
        <h3 className="font-display text-xl md:text-2xl text-aviana-ink">{q}</h3>
        <ChevronDown size={20} className={`text-aviana-gold shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="pb-6 pr-12 text-aviana-muted leading-[1.85] text-[16px]">{a}</p>}
    </div>
  );
}

export default function RestorativeAerialYTT() {
  const { user } = useAuth();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const img = usePageImages("restorative-ytt", IMG_DEFAULTS);
  const enroll = () => setCheckoutOpen(true);
  const busy = false;

  return (
    <div data-testid="restorative-ytt-page">
      {checkoutOpen && <GuestCheckoutModal slug={SLUG} price={444} user={user || null} onClose={() => setCheckoutOpen(false)} />}
      <SEO
        title="Restorative Aerial Yoga Teacher Training Online · 25hr YACEP Certification"
        description="Immersive online restorative aerial yoga training with cinematic, high-quality video production and studio-grade sound. A 25-hour Yoga Alliance YACEP-approved aerial yoga certification online with Holly Johnson — 36 asana tutorials, 4 sensory support lectures, printed full-color manual shipped to your door, 1:1 mentorship, and 1 year of access."
        path="/courses/restorative-aerial-yoga-teacher-training"
        image="/social/og-restorative-ytt.jpg"
        keywords="restorative aerial yoga training, aerial yoga certification online, online aerial yoga course, online restorative aerial yoga teacher training, restorative aerial yoga certification, aerial yoga YACEP, 25 hour aerial yoga training, aerial yoga manual, Holly Johnson, Aviana Yoga, learn aerial yoga online, aerial yoga teacher training online, restorative yoga certification, aerial hammock training"
        jsonLd={[
          // 1. Course — the primary listing. Adds inLanguage, timeRequired,
          //    numberOfCredits, and rich provider/instructor context so this
          //    can show up in Google's course carousel.
          {
            "@context": "https://schema.org",
            "@type": "Course",
            "@id": "https://avianayoga.com/courses/restorative-aerial-yoga-teacher-training#course",
            "name": "Restorative Aerial Yoga Teacher Training Online — 25hr YACEP",
            "alternateName": ["Restorative Aerial Yoga Training", "Online Aerial Yoga Certification"],
            "description": "Yoga Alliance YACEP-approved 25-hour online restorative aerial yoga teacher training with cinematic high-quality video production and immersive studio-grade sound design. Includes 36 asana videos, sensory support lectures (color · sound · aromatherapy · chakra), printed full-color manual shipped to your door, 30-minute private 1:1 with Holly, and 1 year of access.",
            "image": "https://avianayoga.com/social/og-restorative-ytt.jpg",
            "url": "https://avianayoga.com/courses/restorative-aerial-yoga-teacher-training",
            "inLanguage": "en",
            "timeRequired": "PT25H",
            "keywords": "restorative aerial yoga training, aerial yoga certification online, online aerial yoga course",
            "about": ["Restorative Aerial Yoga", "Aerial Yoga Teacher Training", "Yoga Alliance YACEP", "Sensory Integration Yoga"],
            "teaches": ["Restorative aerial yoga methodology", "Hammock setup and safety", "Sequencing 90-minute restorative aerial classes", "Sensory support tools — color therapy, sound healing, aromatherapy, chakra integration", "Practice teaching hours and self-evaluation"],
            "educationalLevel": "Continuing Education",
            "educationalCredentialAwarded": "25-hour Yoga Alliance YACEP Certificate",
            "numberOfCredits": { "@type": "QuantitativeValue", "value": 25, "unitText": "Hours" },
            "courseCode": "RAYTT-25",
            "provider": {
              "@type": "Organization",
              "@id": "https://avianayoga.com/#organization",
              "name": "Aviana Yoga",
              "url": "https://avianayoga.com",
              "logo": "https://avianayoga.com/brand/aviana-logo.png",
            },
            "instructor": {
              "@type": "Person",
              "@id": "https://avianayoga.com/#holly",
              "name": "Holly Johnson",
              "jobTitle": "Founder & Lead Trainer, Aviana Yoga",
              "description": "E-RYT 500 aerial yoga teacher trainer, Usui-Holy Fire III Reiki Master, and author of Soaring with the Soul.",
              "url": "https://avianayoga.com/about",
            },
            "offers": {
              "@type": "Offer",
              "price": "444",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "url": "https://avianayoga.com/courses/restorative-aerial-yoga-teacher-training",
              "category": "Online course",
            },
            "hasCourseInstance": {
              "@type": "CourseInstance",
              "courseMode": "Online",
              "courseWorkload": "PT25H",
              "instructor": { "@id": "https://avianayoga.com/#holly" },
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5",
              "reviewCount": String(COURSE_TESTIMONIALS.length),
              "bestRating": "5",
              "worstRating": "5",
            },
            "review": COURSE_TESTIMONIALS.map((t) => ({
              "@type": "Review",
              "author": { "@type": "Person", "name": t.name },
              "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
              "reviewBody": t.quote,
            })),
          },
          // 2. FAQPage — enables the expandable FAQ rich result in Google
          //    SERP, one of the biggest CTR levers available.
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": FAQS.map((f) => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": { "@type": "Answer", "text": f.a },
            })),
          },
          // 3. VideoObject — the welcome preview reel. Helps the page qualify
          //    for Google's Video result carousel.
          {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            "name": "Welcome — Restorative Aerial Yoga Teacher Training Online",
            "description": "A 2-minute welcome from Holly Johnson previewing the immersive video production, sound design, and warmth inside the Aviana Online Restorative Aerial Yoga Teacher Training.",
            "thumbnailUrl": "https://avianayoga.com/social/og-restorative-ytt.jpg",
            "uploadDate": "2024-03-01",
            "contentUrl": "https://player.vimeo.com/video/927293789",
            "embedUrl": "https://player.vimeo.com/video/927293789",
            "publisher": { "@id": "https://avianayoga.com/#organization" },
          },
          // 4. BreadcrumbList — helps Google render breadcrumbs in SERP
          //    instead of the raw URL.
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://avianayoga.com/" },
              { "@type": "ListItem", "position": 2, "name": "Courses", "item": "https://avianayoga.com/courses" },
              { "@type": "ListItem", "position": 3, "name": "Restorative Aerial Yoga Teacher Training Online", "item": "https://avianayoga.com/courses/restorative-aerial-yoga-teacher-training" },
            ],
          },
          // 5. HowTo — Google surfaces HowTo cards for procedural queries
          //    like "how to become a certified aerial yoga teacher online".
          //    Ranks well because most competing pages don't publish one.
          {
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "How to become a certified Restorative Aerial Yoga teacher online",
            "description": "Complete Aviana's 25-hour Yoga Alliance YACEP-approved Restorative Aerial Yoga Teacher Training online.",
            "totalTime": "P90D",
            "estimatedCost": { "@type": "MonetaryAmount", "currency": "USD", "value": "444" },
            "supply": ["Aerial yoga hammock at recommended rigging height", "A yoga mat and props"],
            "tool": ["Laptop, tablet, or phone with internet access"],
            "step": [
              { "@type": "HowToStep", "position": 1, "name": "Enroll", "text": "Register at avianayoga.com/courses/restorative-aerial-yoga-teacher-training. Your login and course access arrive by email within minutes." },
              { "@type": "HowToStep", "position": 2, "name": "Study 36 asana tutorials", "text": "Work through 36 cinematically-produced restorative aerial yoga asanas, each with a detailed teaching breakdown." },
              { "@type": "HowToStep", "position": 3, "name": "Complete sensory support modules", "text": "Learn to integrate color therapy, sound healing, aromatherapy, and chakra-based sequencing into your classes." },
              { "@type": "HowToStep", "position": 4, "name": "Log practice teaching hours", "text": "Teach the material to friends or students and log your practice hours in the built-in tracker." },
              { "@type": "HowToStep", "position": 5, "name": "1:1 mentorship with Holly", "text": "Schedule your 30-minute private call with Holly to review your work and answer questions." },
              { "@type": "HowToStep", "position": 6, "name": "Receive your 25-hour YACEP certificate", "text": "Once every milestone is complete you're issued a Yoga Alliance YACEP-approved certificate you can register directly with Yoga Alliance." },
            ],
          },
        ]}
      />
      {/* HERO */}
      <section className="relative min-h-[92svh] flex items-center overflow-hidden bg-aviana-plumDeep text-aviana-cream">
        <img src={img("hero")} alt="Woman in restorative aerial yoga pose supported by a silk hammock" loading="eager" fetchPriority="high" className="absolute inset-0 w-full h-full object-cover object-center opacity-80" />
        {/* Lightened overlay — image is more visible now */}
        <div className="absolute inset-0 bg-gradient-to-r from-aviana-plumDeep/85 via-aviana-plumDeep/55 to-aviana-plumDeep/10" />
        <div className="absolute inset-0 grain pointer-events-none opacity-30" aria-hidden />
        <Mandala size={580} variant="full" className="absolute -left-40 top-[68%] text-aviana-gold opacity-[0.10] animate-spin-slow pointer-events-none" />

        <div className="container-aviana relative z-10 py-28">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-7">
              <Mandala size={28} variant="lotus" className="text-aviana-goldSoft" />
              <p className="overline text-aviana-goldSoft">Restorative aerial yoga training · 25-hour certification · Lifetime access · Global</p>
            </div>
            <p className="font-serif italic text-[18px] md:text-[20px] text-aviana-goldSoft mb-4" data-testid="ytt-keyword-line">
              Restorative Aerial Yoga Teacher Training Online
            </p>
            <h1 className="font-display text-[52px] sm:text-[72px] lg:text-[92px] leading-[0.95]" data-testid="ytt-headline">
              Hold your clients
              <br />
              <em className="italic font-serif gold-text">more deeply.</em>
            </h1>
            <p className="mt-8 font-serif italic text-[20px] md:text-[24px] leading-[1.45] text-aviana-cream/90 max-w-xl" data-testid="ytt-hero-tagline">
              For instructors and healers ready to expand their practice.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <button onClick={enroll} disabled={busy} className="btn-gold" data-testid="hero-enroll-cta">
                {busy ? "Loading…" : <>Enroll · $444 <ArrowRight size={16} /></>}
              </button>
              <a href="#curriculum" className="px-8 py-3.5 rounded-full border border-aviana-goldSoft/50 text-aviana-goldSoft hover:bg-aviana-goldSoft hover:text-aviana-plumDeep text-sm tracking-[0.18em] uppercase transition-all duration-500 inline-flex items-center gap-2">
                See the curriculum
              </a>
            </div>
            <div className="mt-14 flex flex-wrap items-center gap-7 text-aviana-cream/60 text-[11px] tracking-[0.28em] uppercase">
              <span>One-time $444</span>
              <span className="w-6 h-px bg-aviana-goldSoft/40" />
              <span>Lifetime access</span>
              <span className="w-6 h-px bg-aviana-goldSoft/40" />
              <span>Full color manual shipped</span>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR — reinforces credibility right after the hero. Keeps every
          "why should I trust this?" answer in one horizontal strip so it can
          be scanned in under 3 seconds. Big lift on cold-traffic conversion. */}
      <section className="bg-aviana-cream border-y border-aviana-border py-6" data-testid="trust-bar">
        <div className="container-aviana">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4 items-center text-center">
            {[
              { k: "Certificate", v: "25 Hour Restorative Aerial Yoga Teacher Training" },
              { k: "25 Hours", v: "Self-paced, online" },
              { k: "Global", v: "Students in 12+ countries" },
              { k: "Lifetime Access", v: "Watch on your schedule" },
            ].map((t) => (
              <div key={t.k} className="flex flex-col items-center">
                <p className="font-display text-lg md:text-xl text-aviana-ink leading-tight">{t.k}</p>
                <p className="text-[10px] tracking-[0.2em] uppercase text-aviana-muted mt-1.5">{t.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="pt-20 md:pt-28 pb-4 md:pb-6 bg-aviana-paper" data-testid="who-for-section">
        <div className="container-aviana max-w-3xl">
          <p className="overline-plum mb-5">Who this is for</p>
          <div className="flex flex-wrap gap-2.5 mb-9" data-testid="who-for-labels">
            {["Yoga & Aerial Instructors", "Massage Therapists", "Chiropractors", "Trauma Therapists"].map((l) => (
              <span key={l} className="px-4 py-2 rounded-full border border-aviana-gold/50 bg-aviana-cream text-[11px] tracking-[0.18em] uppercase text-aviana-plum">{l}</span>
            ))}
          </div>
          <p className="font-serif italic text-[21px] md:text-[24px] leading-[1.5] text-aviana-inkSoft" data-testid="ytt-hero-p1">
            For yoga and aerial yoga instructors, massage therapists, chiropractors, trauma therapists and every healer whose work begins with safety — this <strong className="not-italic font-medium text-aviana-ink">25 Hour Restorative Aerial Yoga Teacher Training</strong> gives you a gentle, powerful new way to support the people who come to you: fully held, deeply supported, finally able to let go.
          </p>
          <p className="mt-6 text-aviana-muted leading-[1.9] text-[17px]" data-testid="ytt-hero-p2">
            Weave restorative aerial yoga into the practice you already have — as a stand-alone class, a private session, or a soft bridge between your hands-on work and true nervous-system rest. Learn at your own pace, from anywhere, and step into this work with quiet confidence.
          </p>
        </div>
      </section>

      {/* FEATURED SNIPPET TARGET — a concise 45-word definition of the
          subject, formatted so Google's AI Overviews / featured-snippet
          extractors can lift it verbatim into the SERP box. This is one of
          the single highest-leverage on-page SEO plays. */}
      <section className="py-14 bg-aviana-paper" data-testid="featured-snippet">
        <div className="container-aviana max-w-3xl">
          <div className="rounded-2xl border-l-4 border-aviana-gold bg-aviana-cream p-6 sm:p-8">
            <p className="overline-plum mb-3">What is Restorative Aerial Yoga?</p>
            <p className="text-aviana-muted text-[17px] leading-[1.9]" data-testid="featured-snippet-text">
              <strong className="text-aviana-ink font-medium">Restorative aerial yoga</strong> is a slow-paced, therapeutic style of aerial yoga that uses a fabric hammock to fully support the body in passive, deeply-held postures. Combining traditional restorative yoga principles with the weightless suspension of aerial silks, it releases tension in the spine, hips, and shoulders while calming the nervous system — ideal for stress recovery, chronic pain, and sensory integration.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-28 bg-aviana-paper relative overflow-hidden" data-testid="welcome-video-section">
        <Mandala size={420} variant="lotus" className="absolute -right-24 -top-12 text-aviana-gold opacity-[0.07] animate-spin-slow pointer-events-none hidden md:block" />
        <Mandala size={280} variant="rose" className="absolute -left-16 -bottom-12 text-aviana-plum opacity-[0.08] pointer-events-none hidden md:block" />
        <div className="container-aviana max-w-4xl relative">
          <div className="text-center mb-10">
            <p className="overline-plum mb-4">A taste of the practice</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">
              Watch Holly's <em className="italic font-serif gold-text">welcome.</em>
            </h2>
            <p className="mt-5 text-aviana-muted text-[15px] max-w-xl mx-auto leading-[1.7]">
              Two minutes inside the program — a preview of the <strong className="text-aviana-ink font-medium">cinematic video production and studio-grade sound design</strong> that makes this <em>online aerial yoga course</em> feel like being in the room with Holly for the full 25 hours.
            </p>
          </div>
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-aviana-gold/30 bg-aviana-plumDeep">
            <VimeoWithPoster
              posterSrc={img("welcome-poster")}
              embedSrc="https://player.vimeo.com/video/927293789?h=5da240c863&badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1"
              title="Welcome Video — Restorative Aerial Yoga Teacher Training"
            />
          </div>
          <p className="text-center mt-6 text-[11px] tracking-[0.22em] uppercase text-aviana-muted">~2 min · No commitment · See the energy first</p>
        </div>
      </section>

      {/* WHY THIS COURSE */}
      <section className="py-28 md:py-36">
        <div className="container-aviana max-w-6xl grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
          <p className="overline-plum mb-5">A program designed with you in mind</p>
          <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.05]">
            Created for the <em className="italic font-serif gold-text">growing call to be held.</em>
          </h2>
          <p className="mt-10 font-serif italic text-[22px] md:text-[24px] leading-[1.5] text-aviana-inkSoft">
            We strive to <em>set each instructor up for success.</em>
          </p>
          <p className="mt-7 text-aviana-muted leading-[1.95] text-[17px]">
            This program was created in response to the growing number of people wanting to expand their offerings for students who <em>crave to be held</em> and restored to a place of wholeness and balance within. At Aviana, we deeply value the opportunity to connect with instructors in person — and yet it's just not possible for us to be everywhere at once to meet the enthusiasm for this practice. So we built an <strong className="text-aviana-ink font-medium">aerial yoga certification online</strong> that gives you <strong className="text-aviana-ink font-medium">everything you need to feel confident</strong> in offering <em>Restorative Aerial Yoga</em> to your students — or to explore the practice just for yourself.
          </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-[2rem] overflow-hidden ring-1 ring-aviana-gold/30 shadow-xl aspect-[4/5]" data-testid="why-photo">
              <img src={img("why-photo")} alt="Essential oils used for sensory support in restorative aerial yoga" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU'LL RECEIVE */}
      <section className="py-24 md:py-32 bg-aviana-surface">
        <div className="container-aviana">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="overline mb-5">What you'll receive</p>
            <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.02]">
              Everything you need to <em className="italic font-serif gold-text">begin teaching.</em>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-5 max-w-4xl mx-auto">
            {INCLUDED.map((item) => (
              <div key={item} className="flex gap-4 items-start">
                <span className="text-aviana-gold mt-1 shrink-0"><Check size={18} strokeWidth={2.5} /></span>
                <p className="text-aviana-inkSoft leading-[1.7] text-[16px]">{item}</p>
              </div>
            ))}
          </div>
          <p className="mt-14 text-center text-[12px] tracking-[0.22em] uppercase text-aviana-muted">
            Outside the US? Shipping for your printed manual is $30–60 USD additional.
          </p>
        </div>
      </section>

      {/* PRIVATE CALL WITH HOLLY */}
      <section className="py-24 md:py-32 bg-aviana-cream relative overflow-hidden" data-testid="private-call-section">
        <Mandala size={360} variant="lotus" className="absolute -right-20 -bottom-16 text-aviana-gold opacity-[0.08] pointer-events-none hidden md:block" />
        <div className="container-aviana max-w-5xl relative grid md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-5">
            <div className="rounded-[2rem] overflow-hidden ring-1 ring-aviana-gold/30 shadow-xl aspect-[4/3]">
              <img src={img("founder-call")} alt="Holly Johnson resting in a restorative aerial yoga pose" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-7">
            <span className="inline-block px-4 py-1.5 rounded-full bg-aviana-gold/15 border border-aviana-gold/40 text-[10px] tracking-[0.24em] uppercase text-aviana-plum mb-6" data-testid="private-call-badge">
              Included with every enrollment
            </span>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">
              Personal time with the founder of <em className="italic font-serif gold-text">the Aviana Method.</em>
            </h2>
            <p className="mt-7 text-aviana-muted leading-[1.9] text-[17px]">
              Every enrollment includes a <strong className="text-aviana-ink font-medium">private 30-minute call with Holly</strong>. Bring your questions, your practice, your vision — and receive her guidance on bringing restorative aerial yoga into your work.
            </p>
          </div>
        </div>
      </section>

      {/* CURRICULUM ACCORDION */}
      <section id="curriculum" className="py-28 md:py-36">
        <div className="container-aviana max-w-5xl">
          <figure className="mb-16 rounded-[2rem] overflow-hidden ring-1 ring-aviana-gold/30 shadow-xl aspect-[16/7]" data-testid="curriculum-photo">
            <img src={img("curriculum-photo")} alt="Holly teaching the chakras and energy centers" loading="lazy" className="w-full h-full object-cover" />
          </figure>
          <div className="grid lg:grid-cols-12 gap-14 mb-16 items-end">
            <div className="lg:col-span-7">
              <p className="overline mb-5">The curriculum</p>
              <h2 className="font-display text-5xl md:text-7xl text-aviana-ink leading-[1.02]">
                Five <em className="italic font-serif gold-text">pillars.</em>
              </h2>
            </div>
            <p className="lg:col-span-5 font-serif italic text-aviana-muted text-[18px] leading-[1.6]">
              Each pillar builds on the last — from <em>foundational principles</em> to full-class sensory integration.
            </p>
          </div>
          <div className="space-y-3">
            {CURRICULUM.map((c, i) => (
              <details key={c.title} className="group bg-aviana-surface rounded-[1.5rem] border border-aviana-border overflow-hidden">
                <summary className="cursor-pointer p-7 flex items-center gap-6 list-none hover:bg-aviana-cream transition-colors">
                  <span className="font-display text-3xl text-aviana-gold w-12">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-2xl md:text-3xl text-aviana-ink flex-1">{c.title}</h3>
                  <span className="text-aviana-gold text-2xl transition-transform group-open:rotate-45">+</span>
                </summary>
                <div className="px-7 pb-7 pl-[88px] space-y-4">
                  <p className="text-aviana-muted leading-[1.85] text-[16px]">{c.body}</p>
                  <ul className="space-y-2.5">
                    {c.bullets.map((b) => (
                      <li key={b} className="flex gap-3 text-aviana-inkSoft text-[14px] leading-[1.65]">
                        <span className="text-aviana-gold mt-[7px] text-[8px]">◆</span>{b}
                      </li>
                    ))}
                  </ul>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CERTIFICATION REQUIREMENTS */}
      <section className="py-28 md:py-36 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none opacity-60" aria-hidden />
        <Mandala size={700} variant="full" className="absolute -right-20 top-1/2 -translate-y-1/2 text-aviana-gold opacity-[0.08] animate-spin-slow" />
        <div className="container-aviana relative max-w-5xl">
          <div className="grid lg:grid-cols-12 gap-14 items-start">
            <div className="lg:col-span-5">
              <p className="overline mb-5 text-aviana-goldSoft">Certification</p>
              <h2 className="font-display text-5xl md:text-6xl leading-[1.02]">
                The four modules to <em className="italic font-serif gold-text">earn your wings.</em>
              </h2>
              <p className="mt-9 font-serif italic text-aviana-cream/85 text-[18px] leading-[1.6]">
                To receive your 25-hour Restorative Aerial Yoga Instructor certification, you'll submit four modules to satisfaction after watching, practicing, and reading all the training materials.
              </p>
              <p className="mt-6 text-aviana-cream/70 leading-[1.85] text-[14px]">
                Once submitted, allow 2 weeks for review and feedback. You have one year from enrollment to complete your certification work, with lifetime access to the videos and materials.
              </p>
            </div>
            <div className="lg:col-span-7 space-y-4">
              {CERTIFICATION_MODULES.map((m) => (
                <div key={m.num} className="rounded-[1.5rem] border border-aviana-gold/30 p-7 bg-aviana-plumDeep/50">
                  <div className="flex items-start gap-5">
                    <span className="font-display text-4xl text-aviana-goldSoft">{m.num}</span>
                    <div>
                      <h3 className="font-display text-2xl text-aviana-cream">{m.title}</h3>
                      <p className="mt-2 text-aviana-cream/80 leading-[1.75] text-[15px]">{m.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* REQUIREMENTS */}
      <section className="py-24 md:py-32">
        <div className="container-aviana max-w-4xl">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="relative max-w-md">
                <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-aviana-goldSoft to-aviana-plum opacity-20 blur-2xl" />
                <div className="relative aspect-square rounded-[1.5rem] overflow-hidden ring-2 ring-aviana-gold/40">
                  <img src={img("requirements-photo")} alt="Restorative aerial yoga practice in the hammock" loading="lazy" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="overline-plum mb-5">Requirements</p>
              <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">
                What you'll <em className="italic font-serif gold-text">need.</em>
              </h2>
              <p className="mt-8 text-aviana-muted leading-[1.85] text-[16px]">
                Ideally, access to at least <strong className="text-aviana-ink font-medium">2 hammock setups</strong> so you can complete your practice teaching. One setup is workable if your student is comfortable being demonstrated on the same fabric they'll be using.
              </p>
              <p className="mt-5 text-aviana-muted leading-[1.85] text-[16px]">
                Your <strong className="text-aviana-ink font-medium">5 practice teaching hours</strong> can be completed with just 1 student — though we encourage teaching a variety of bodies so you can learn how different people connect with the fabric.
              </p>
              <p className="mt-5 text-aviana-muted leading-[1.85] text-[16px]">
                Upon purchase, you'll receive a video covering equipment needs including <em>options for tying the hammocks.</em>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-aviana-surface">
        <div className="container-aviana max-w-5xl">
          <div className="text-center mb-14">
            <p className="overline mb-5">From past students</p>
            <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.02]">
              They <em className="italic font-serif gold-text">found their wings.</em>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {COURSE_TESTIMONIALS.map((t) => (
              <figure key={t.name} className="bg-aviana-cream rounded-2xl p-9 border border-aviana-border">
                <Mandala size={32} variant="rose" className="text-aviana-gold mb-5 opacity-80" />
                <blockquote className="font-serif italic text-[18px] leading-[1.6] text-aviana-inkSoft">"{t.quote}"</blockquote>
                <figcaption className="mt-7 pt-5 border-t border-aviana-border">
                  <p className="font-display text-xl text-aviana-ink">{t.name}</p>
                  <p className="text-[11px] tracking-[0.22em] uppercase text-aviana-muted/80 mt-1">{t.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* RESTORATIVE BAND */}
      <section className="bg-aviana-surface pb-6" data-testid="restorative-band">
        <div className="container-aviana max-w-6xl">
          <div className="rounded-[2rem] overflow-hidden ring-1 ring-aviana-gold/30 aspect-[16/9] md:aspect-[2/1]">
            <img src={img("restorative-band")} alt="Deep rest in a restorative aerial yoga hammock" loading="lazy" className="w-full h-full object-cover object-[50%_72%]" />
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-28 md:py-36">
        <div className="container-aviana max-w-3xl text-center">
          <p className="overline mb-5">Tuition</p>
          <h2 className="font-display text-5xl md:text-7xl text-aviana-ink leading-[1.02]">
            One time. <em className="italic font-serif gold-text">$444.</em>
          </h2>
          <p className="mt-9 font-serif italic text-aviana-muted text-[18px] leading-[1.6]">
            Includes lifetime access and your printed full-color training manual shipped to your door. Non-refundable. International shipping may vary.
          </p>
          <div className="mt-12">
            <button onClick={enroll} disabled={busy} className="btn-gold" data-testid="pricing-enroll-cta">
              {busy ? "Loading…" : <>Buy now <ArrowRight size={16} /></>}
            </button>
            <p className="mt-5 text-[11px] tracking-[0.22em] uppercase text-aviana-muted/80">Secure Stripe checkout · Instant access on payment</p>
          </div>
        </div>
      </section>

      <InsuranceCallout />

      {/* FAQ */}
      <section className="py-28 md:py-36 bg-aviana-surface">
        <div className="container-aviana max-w-4xl">
          <div className="text-center mb-14">
            <p className="overline mb-5">Frequently asked</p>
            <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.02]">
              Questions, <em className="italic font-serif gold-text">honored.</em>
            </h2>
          </div>
          <div className="bg-aviana-cream rounded-[2rem] border border-aviana-border px-9 md:px-12">
            {FAQS.map((f, i) => <FaqItem key={f.q} q={f.q} a={f.a} idx={i} />)}
          </div>
        </div>
      </section>

      {/* CONTINUE EXPLORING — internal-linking hub that builds topical
          authority (Google rewards clusters of related content) and gives
          visitors who aren't ready to buy a next step other than the back
          button. Each link is keyword-rich anchor text. */}
      <section className="py-20 md:py-24 bg-aviana-cream" data-testid="related-reading">
        <div className="container-aviana max-w-5xl">
          <div className="text-center mb-10">
            <p className="overline-plum mb-3">Continue exploring</p>
            <h2 className="font-display text-3xl md:text-4xl text-aviana-ink leading-tight">
              Learn more about the practice
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { to: "/journal/the-physical-mental-and-emotional-benefits-of-aerial-yoga", title: "Benefits of Aerial Yoga", sub: "Physical, mental and emotional — what regular practice unlocks." },
              { to: "/journal/aerial-yoga-to-open-your-heart", title: "Aerial Yoga to Open Your Heart", sub: "Why the sacred surrender of the hammock is a heart-opener." },
              { to: "/about", title: "Meet Holly Johnson", sub: "E-RYT 500 aerial yoga teacher trainer & author of Soaring with the Soul." },
            ].map((c) => (
              <Link key={c.to} to={c.to} className="group block bg-white rounded-2xl border border-aviana-border p-6 hover:border-aviana-plum transition-colors" data-testid={`related-${c.to.split("/").pop()}`}>
                <p className="font-display text-xl text-aviana-ink leading-tight">{c.title}</p>
                <p className="text-[13px] text-aviana-muted mt-2 leading-[1.55]">{c.sub}</p>
                <p className="mt-4 text-[11px] tracking-[0.22em] uppercase text-aviana-plum group-hover:text-aviana-gold">Read →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOLLY CLOSING */}
      <section className="py-28 md:py-36 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none opacity-60" aria-hidden />
        <Mandala size={700} variant="full" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-aviana-gold opacity-[0.10] animate-spin-slow" />
        <div className="container-aviana relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4">
            <div className="relative max-w-xs mx-auto">
              <div className="absolute -inset-3 rounded-full bg-gradient-to-br from-aviana-goldSoft to-aviana-plum opacity-30 blur-2xl" />
              <div className="relative aspect-square rounded-full overflow-hidden ring-2 ring-aviana-gold/40">
                <img src={img("closing-portrait")} alt="A student resting in a restorative aerial yoga hammock" loading="lazy" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-8 text-center lg:text-left">
            <p className="overline mb-5 text-aviana-goldSoft">A note from your guide</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05]">
              You don't have to wait for an in-person training to <em className="italic font-serif gold-text">begin.</em>
            </h2>
            <p className="mt-7 font-serif italic text-aviana-cream/90 leading-[1.6] text-[19px]">
              This program is the medicine I've been teaching for over a decade — distilled, sequenced, and made <em>portable.</em> Take it at your pace. Reach out when you need me. And when you're ready, share it with the women who need to be held.
            </p>
            <p className="mt-6 text-[11px] tracking-[0.28em] uppercase text-aviana-goldSoft">— Holly</p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center lg:justify-start">
              <button onClick={enroll} disabled={busy} className="btn-gold" data-testid="closing-enroll-cta">
                {busy ? "Loading…" : <>Enroll · $444 <ArrowRight size={16} /></>}
              </button>
              <Link to="/courses" className="text-[12px] tracking-[0.22em] uppercase text-aviana-goldSoft hover:text-aviana-cream self-center">
                See all courses →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STICKY MOBILE ENROLL BAR — always-visible CTA on phones so students
          never have to scroll back to buy. Hidden on desktop where the primary
          buttons are already visible above the fold. */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-aviana-cream/95 backdrop-blur-md border-t border-aviana-border shadow-[0_-4px_20px_rgba(60,20,60,0.08)] px-4 py-3 flex items-center gap-3" data-testid="mobile-sticky-cta">
        <div className="flex-1 min-w-0">
          <p className="font-figure text-xl text-aviana-ink leading-none">$444</p>
          <p className="text-[10px] tracking-[0.18em] uppercase text-aviana-muted mt-0.5">One time · Lifetime access</p>
        </div>
        <button onClick={enroll} disabled={busy} className="btn-gold !py-3 !px-6 !text-[11px] flex-shrink-0" data-testid="sticky-enroll-cta">
          {busy ? "Loading…" : <>Enroll now <ArrowRight size={14} /></>}
        </button>
      </div>
    </div>
  );
}

// Vimeo welcome wrapper — engineered so the video is READY the moment
// Holly's audience scrolls to it. Strategy:
//   1. Load Vimeo's Player.js SDK once, on component mount.
//   2. Instantiate the iframe IMMEDIATELY with `autoplay=1&muted=1` so it
//      begins buffering and playing silently the second the page loads.
//      No scroll trigger, no click required — Vimeo's player is running
//      before the visitor ever sees it.
//   3. Overlay the on-brand poster on top of the iframe until Player.js
//      fires the "playing" event, then fade the poster away. This kills
//      the black-screen lag: whatever Vimeo shows while loading is hidden
//      behind Holly's poster.
//   4. When the section enters the viewport, ask Player.js to unmute
//      so the audience gets sound. If the browser blocks
//      the unmute (Chrome/Safari require prior user interaction), we show
//      a small "Tap for sound" button as a fallback.
function VimeoWithPoster({ posterSrc, embedSrc, title }) {
  const iframeRef = React.useRef(null);
  const playerRef = React.useRef(null);
  const wrapRef = React.useRef(null);
  const [posterVisible, setPosterVisible] = React.useState(true);
  const [needsUnmuteTap, setNeedsUnmuteTap] = React.useState(false);
  const unmuteAttemptedRef = React.useRef(false);

  // Muted, autoplaying, buffering-ready iframe URL — mounts on load so
  // the player is warm before the visitor reaches the section.
  const preloadedEmbed = React.useMemo(() => {
    try {
      const url = new URL(embedSrc);
      url.searchParams.set("autoplay", "1");
      url.searchParams.set("muted", "1");
      url.searchParams.set("playsinline", "1");
      url.hash = "t=10s";
      return url.toString();
    } catch {
      const sep = embedSrc.includes("?") ? "&" : "?";
      return `${embedSrc}${sep}autoplay=1&muted=1&playsinline=1#t=10s`;
    }
  }, [embedSrc]);

  // Load Player.js SDK once (idempotent — subsequent mounts reuse window.Vimeo).
  React.useEffect(() => {
    const attach = () => {
      if (!window.Vimeo || !iframeRef.current) return;
      const player = new window.Vimeo.Player(iframeRef.current);
      playerRef.current = player;
      // As soon as the first frame is actually playing, fade the poster.
      player.on("playing", () => setPosterVisible(false));
      player.on("bufferend", () => setPosterVisible(false));
    };

    if (window.Vimeo && window.Vimeo.Player) {
      attach();
      return;
    }
    const existing = document.querySelector('script[data-vimeo-player-sdk]');
    if (existing) {
      existing.addEventListener("load", attach, { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = "https://player.vimeo.com/api/player.js";
    script.async = true;
    script.setAttribute("data-vimeo-player-sdk", "1");
    script.addEventListener("load", attach, { once: true });
    document.body.appendChild(script);
  }, []);

  // Try to unmute the moment the section enters the viewport.
  React.useEffect(() => {
    const node = wrapRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;

    const tryUnmute = async () => {
      if (unmuteAttemptedRef.current) return;
      const player = playerRef.current;
      if (!player) return;
      unmuteAttemptedRef.current = true;
      try {
        await player.setMuted(false);
        await player.setVolume(1);
        await player.setCurrentTime(10);
        await player.play();
        setNeedsUnmuteTap(false);
      } catch {
        // Browser autoplay policy blocked the unmute — surface the tap-to-sound button.
        setNeedsUnmuteTap(true);
      }
    };

    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          tryUnmute();
          io.disconnect();
          break;
        }
      }
    }, { threshold: 0.4 });
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const handleUnmuteTap = async () => {
    const player = playerRef.current;
    if (!player) return;
    try {
      await player.setMuted(false);
      await player.setVolume(1);
      await player.play();
      setNeedsUnmuteTap(false);
    } catch {
      /* user will still see the Vimeo built-in controls to unmute */
    }
  };

  return (
    <div ref={wrapRef} style={{ padding: "56.25% 0 0 0", position: "relative" }}>
      <iframe
        ref={iframeRef}
        src={preloadedEmbed}
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
        title={title}
        data-testid="welcome-vimeo-iframe"
      />
      {/* Poster overlay — sits on top of the iframe and fades out as soon
          as the player fires 'playing'. Keeps Vimeo's black loading frame
          entirely hidden from view. */}
      <div
        className={`absolute inset-0 pointer-events-none transition-opacity duration-500 ${posterVisible ? "opacity-100" : "opacity-0"}`}
        aria-hidden="true"
        data-testid="welcome-vimeo-poster-overlay"
      >
        <img
          src={posterSrc}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-aviana-plumDeep/50 via-transparent to-transparent" />
      </div>
      {needsUnmuteTap && !posterVisible && (
        <button
          onClick={handleUnmuteTap}
          className="absolute bottom-4 right-4 z-10 bg-aviana-gold/95 text-aviana-plumDeep rounded-full px-4 py-2 shadow-xl text-[11px] tracking-[0.22em] uppercase font-medium hover:scale-105 transition-transform"
          data-testid="welcome-vimeo-unmute"
          aria-label="Enable sound"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true" className="inline-block mr-1.5 -mt-0.5">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
          </svg>
          Tap for sound
        </button>
      )}
    </div>
  );
}
